import { useState } from 'react'
import { clsx } from 'clsx'
import PhotoUpload from './PhotoUpload'
import VideoRecorder from './VideoRecorder'
import MoodCheck from './MoodCheck'
import { wsManager } from '../../api/websocket'

export type ProactiveType =
  | 'mood_check'
  | 'selfie_request'
  | 'video_checkin'
  | 'gentle_ping'
  | 'daily_reflection'
  | 'goal_checkin'
  | 'consilium_proposal'

export interface ProactiveButton {
  text: string
  action: string
  value?: string | number
}

export interface ProactivePromptData {
  id: number
  type: ProactiveType
  message: string
  therapist: string
  buttons?: ProactiveButton[]
  timestamp: string
}

interface ProactivePromptProps {
  prompt: ProactivePromptData
  onRespond: (action: string, value?: string | number) => void
  onDismiss: () => void
  therapistType?: string
}

export default function ProactivePrompt({
  prompt,
  onRespond,
  onDismiss,
  therapistType = 'wendy',
}: ProactivePromptProps) {
  const [showPhotoUpload, setShowPhotoUpload] = useState(false)
  const [showVideoRecorder, setShowVideoRecorder] = useState(false)
  const [showMoodCheck, setShowMoodCheck] = useState(false)
  const [isResponding, setIsResponding] = useState(false)

  const accentColor = {
    wendy: 'bg-wendy',
    artem: 'bg-artem',
    finch: 'bg-finch',
    una: 'bg-una',
  }[therapistType] || 'bg-primary-500'

  const borderColor = {
    wendy: 'border-wendy',
    artem: 'border-artem',
    finch: 'border-finch',
    una: 'border-una',
  }[therapistType] || 'border-primary-500'

  const handleButtonClick = async (action: string, value?: string | number) => {
    // Handle special actions
    if (action === 'open_camera' || action === 'selfie') {
      setShowPhotoUpload(true)
      return
    }
    if (action === 'open_video' || action === 'video') {
      setShowVideoRecorder(true)
      return
    }
    if (action === 'mood_scale') {
      setShowMoodCheck(true)
      return
    }

    // Handle consilium start
    if (action === 'start_consilium') {
      wsManager.startConsilium()
      onDismiss()
      return
    }

    // Handle dismiss/later
    if (action === 'dismiss' || action === 'later') {
      onDismiss()
      return
    }

    // Send response
    setIsResponding(true)
    try {
      await onRespond(action, value)
    } finally {
      setIsResponding(false)
    }
  }

  const handlePhotoSuccess = () => {
    setShowPhotoUpload(false)
    onRespond('selfie_sent')
  }

  const handleVideoSuccess = () => {
    setShowVideoRecorder(false)
    onRespond('video_sent')
  }

  const handleMoodSuccess = () => {
    setShowMoodCheck(false)
    onRespond('mood_sent')
  }

  // Get default buttons based on prompt type
  const getDefaultButtons = (): ProactiveButton[] => {
    switch (prompt.type) {
      case 'mood_check':
        return [
          { text: 'Оценить настроение', action: 'mood_scale' },
          { text: 'Позже', action: 'later' },
        ]
      case 'selfie_request':
        return [
          { text: 'Отправить селфи', action: 'open_camera' },
          { text: 'Не сейчас', action: 'later' },
        ]
      case 'video_checkin':
        return [
          { text: 'Записать видео', action: 'open_video' },
          { text: 'Позже', action: 'later' },
        ]
      case 'gentle_ping':
        return [
          { text: 'Да, поговорим', action: 'yes' },
          { text: 'Позже', action: 'later' },
        ]
      case 'daily_reflection':
        return [
          { text: 'Рассказать', action: 'yes' },
          { text: 'Пропустить', action: 'skip' },
        ]
      case 'goal_checkin':
        return [
          { text: 'Обновить прогресс', action: 'update_goals' },
          { text: 'Позже', action: 'later' },
        ]
      case 'consilium_proposal':
        return [
          { text: 'Давайте', action: 'start_consilium' },
          { text: 'Позже', action: 'later' },
        ]
      default:
        return [
          { text: 'OK', action: 'ok' },
          { text: 'Закрыть', action: 'dismiss' },
        ]
    }
  }

  const buttons = prompt.buttons || getDefaultButtons()

  // Get icon based on prompt type
  const getIcon = () => {
    switch (prompt.type) {
      case 'mood_check':
        return (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        )
      case 'selfie_request':
        return (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
            />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
        )
      case 'video_checkin':
        return (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        )
      case 'goal_checkin':
        return (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        )
      case 'consilium_proposal':
        return (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
        )
      default:
        return (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
        )
    }
  }

  return (
    <>
      <div className={clsx(
        'mx-4 mb-4 rounded-2xl overflow-hidden shadow-soft animate-slide-up',
        'border-l-4',
        borderColor,
        'bg-white'
      )}>
        {/* Header */}
        <div className={clsx('px-4 py-3 flex items-center gap-3', accentColor, 'text-white')}>
          {getIcon()}
          <span className="font-medium">
            {prompt.type === 'mood_check' && 'Как настроение?'}
            {prompt.type === 'selfie_request' && 'Покажи себя'}
            {prompt.type === 'video_checkin' && 'Видео-привет'}
            {prompt.type === 'gentle_ping' && 'Привет!'}
            {prompt.type === 'daily_reflection' && 'Итоги дня'}
            {prompt.type === 'goal_checkin' && 'Прогресс целей'}
            {prompt.type === 'consilium_proposal' && 'Консилиум терапевтов'}
          </span>

          {/* Close button */}
          <button
            onClick={onDismiss}
            className="ml-auto p-1 hover:bg-white/20 rounded-full transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <p className="text-neutral-700 mb-4">{prompt.message}</p>

          {/* Buttons */}
          <div className="flex gap-2 flex-wrap">
            {buttons.map((button, index) => (
              <button
                key={index}
                onClick={() => handleButtonClick(button.action, button.value)}
                disabled={isResponding}
                className={clsx(
                  'px-4 py-2.5 rounded-xl font-medium transition-colors min-h-[44px]',
                  index === 0
                    ? `${accentColor} text-white hover:opacity-90`
                    : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200',
                  isResponding && 'opacity-50 cursor-not-allowed'
                )}
              >
                {button.text}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showPhotoUpload && (
        <PhotoUpload
          onClose={() => setShowPhotoUpload(false)}
          onSuccess={handlePhotoSuccess}
        />
      )}

      {showVideoRecorder && (
        <VideoRecorder
          onClose={() => setShowVideoRecorder(false)}
          onSuccess={handleVideoSuccess}
        />
      )}

      {showMoodCheck && (
        <MoodCheck
          onClose={() => setShowMoodCheck(false)}
          onSuccess={handleMoodSuccess}
        />
      )}
    </>
  )
}
